import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, Modal, Radio, Space, Spin, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatCurrencyLocalized } from '../../../utils/currency';
import { formatLocalizedPercent, formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import { formatDateTime } from '../../../utils/dateUtils';
import {
  fetchOrderDeliveryRevertPreview,
  revertOrderDelivery,
  type OrderDeliveryRevertPreview,
  type OrderDeliveryRevertTarget,
} from '../api/orderDeliveryRevertApi';

const { Text } = Typography;

export const DELIVERY_REVERT_MIN_REASON_LENGTH = 10;

interface Props {
  open: boolean;
  orderId: string;
  onClose: () => void;
  /** Called after a successful revert so the page refetches every read model. */
  onReverted: (targetStatus: OrderDeliveryRevertTarget) => Promise<void> | void;
  onError: (error: unknown) => void;
}

/**
 * ADMIN confirmation before leaving DELIVERED. Everything shown here comes from
 * the backend preview: released reservations (not restored), WZ and transports
 * with their statuses, and the current margin. The plan snapshot does not change.
 */
export function OrderDeliveryRevertModal({ open, orderId, onClose, onReverted, onError }: Props) {
  const { t, i18n } = useTranslation(['orders', 'transport', 'wz', 'common']);
  const [preview, setPreview] = useState<OrderDeliveryRevertPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [targetStatus, setTargetStatus] = useState<OrderDeliveryRevertTarget>('IN_DELIVERY');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    setReason('');
    setTargetStatus('IN_DELIVERY');
    fetchOrderDeliveryRevertPreview(orderId)
      .then((next) => { if (!cancelled) setPreview(next); })
      .catch(() => { if (!cancelled) { setPreview(null); setLoadFailed(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, orderId]);

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= DELIVERY_REVERT_MIN_REASON_LENGTH;
  const canSubmit = Boolean(preview?.canRevert) && reasonValid && !submitting && !loading;

  const handleSubmit = async () => {
    if (!preview || !canSubmit) return;
    setSubmitting(true);
    try {
      await revertOrderDelivery(orderId, {
        targetStatus,
        reason: trimmedReason,
        expectedUpdatedAt: preview.updatedAt,
      });
      await onReverted(targetStatus);
      onClose();
    } catch (error) {
      onError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderBlockers = () => (preview?.blockers ?? []).map((blocker) => (
    <Alert
      key={blocker.code}
      type="error"
      showIcon
      message={
        blocker.code === 'ORDER_SETTLED_REVERT_BLOCKED'
          ? t('orders:deliveryRevert.errors.settled')
          : blocker.code === 'ORDER_COMMERCIALLY_FROZEN'
            ? t('orders:deliveryRevert.errors.frozen')
            : t('orders:deliveryRevert.errors.blocked')
      }
      description={blocker.settlements && blocker.settlements.length > 0
        ? blocker.settlements.map((settlement) => (
          `${settlement.kind}${settlement.recognizedAt ? ` · ${formatDateTime(settlement.recognizedAt)}` : ''}`
        )).join(', ')
        : undefined}
    />
  ));

  return (
    <Modal
      title={t('orders:deliveryRevert.title')}
      open={open}
      onCancel={onClose}
      maskClosable={false}
      destroyOnClose
      footer={(
        <Space>
          <Button onClick={onClose}>{t('common:cancel')}</Button>
          <Button type="primary" danger disabled={!canSubmit} loading={submitting} onClick={handleSubmit}>
            {t('orders:deliveryRevert.confirm')}
          </Button>
        </Space>
      )}
    >
      {loading && <Spin />}
      {loadFailed && <Alert type="error" showIcon message={t('orders:deliveryRevert.loadFailed')} />}
      {preview && !loading && (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {!preview.isDelivered && <Alert type="warning" showIcon message={t('orders:deliveryRevert.errors.notDelivered')} />}
          {renderBlockers()}

          <Alert
            type="warning"
            showIcon
            message={t('orders:deliveryRevert.earmarksTitle')}
            description={preview.releasedEarmarks.length === 0
              ? t('orders:deliveryRevert.earmarksNone')
              : (
                <div>
                  <div>{t('orders:deliveryRevert.earmarksNotRestored')}</div>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                    {preview.releasedEarmarks.map((earmark) => (
                      <li key={earmark.id}>
                        {earmark.variantName || t('orders:deliveryRevert.unnamedVariant')}
                        {earmark.batchNumber ? ` · ${earmark.batchNumber}` : ''}
                        {' · '}
                        {formatLocalizedQuantity(earmark.quantity, i18n.language)} {earmark.unit ? formatUnit(earmark.unit, i18n.language) : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          />

          <div>
            <Text strong>{t('orders:deliveryRevert.wzTitle')}</Text>
            <div>
              {preview.wzDocuments.length === 0
                ? <Text type="secondary">{t('orders:deliveryRevert.none')}</Text>
                : preview.wzDocuments.map((wz) => (
                  <Tag key={wz.id}>
                    {wz.wzNumber || wz.id} · {t(`wz:status.${wz.status.toLowerCase()}`, { defaultValue: wz.status })}
                  </Tag>
                ))}
            </div>
          </div>

          <div>
            <Text strong>{t('orders:deliveryRevert.transportsTitle')}</Text>
            <div>
              {preview.transports.length === 0
                ? <Text type="secondary">{t('orders:deliveryRevert.none')}</Text>
                : preview.transports.map((transport) => (
                  <Tag key={transport.id}>
                    {transport.routeNumber || transport.id} · {t(`transport:status.${transport.status.toLowerCase()}`, { defaultValue: transport.status })}
                  </Tag>
                ))}
            </div>
          </div>

          <Alert type="info" showIcon message={t('orders:deliveryRevert.planUnchanged')} />

          <div>
            <Text strong>{t('orders:deliveryRevert.currentMargin')}</Text>
            <div>
              {preview.margin.warehouseCostPending
                ? t('orders:deliveryRevert.marginPending')
                : `${formatCurrencyLocalized(preview.margin.margin, 'PLN', i18n.language)} (${formatLocalizedPercent(preview.margin.marginPercent, i18n.language)})`}
            </div>
          </div>

          <div>
            <Text strong>{t('orders:deliveryRevert.targetStatus')}</Text>
            <Radio.Group
              value={targetStatus}
              onChange={(event) => setTargetStatus(event.target.value)}
              style={{ display: 'block', marginTop: 4 }}
            >
              {preview.targetStatuses.map((status) => (
                <Radio key={status} value={status}>{t(`orders:statusOptions.${status}`)}</Radio>
              ))}
            </Radio.Group>
          </div>

          <div>
            <Text strong>{t('orders:deliveryRevert.reason')}</Text>
            <Input.TextArea
              aria-label={t('orders:deliveryRevert.reason')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              placeholder={t('orders:deliveryRevert.reasonPlaceholder')}
            />
            {!reasonValid && (
              <Text type="secondary">{t('orders:deliveryRevert.reasonHint', { min: DELIVERY_REVERT_MIN_REASON_LENGTH })}</Text>
            )}
          </div>
        </Space>
      )}
    </Modal>
  );
}

export default OrderDeliveryRevertModal;
